import { DateTimeValueSchemas } from "@validereinc/utilities";
import { z } from "zod";
import {
  createCreateOneRequestSchema,
  createGetListRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelSchema,
  IdentifierSchema,
} from "../util/index.ts";
import { AttributeSchema } from "./AttributeSchemas.ts";
import { CustomAttributeRecordSchema } from "./CustomAttributeSchemas.ts";
import { FacilitySchema } from "./FacilitySchemas.ts";
import { UnitConfigurationSchema } from "./MeasurementTypeSchemas.ts";

export const DeviceMeasurementSeriesSchema = z
  .object({
    description: z.string(),
    unit: z.string(),
  })
  .merge(
    UnitConfigurationSchema.pick({
      measurement_type: true,
    })
  )
  .merge(DomainModelSchema);
export type DeviceMeasurementSeriesType = z.infer<
  typeof DeviceMeasurementSeriesSchema
>;

export const DeviceAttributeSchema = z
  .object({
    height: z.number(),
    location: z.tuple([
      z.number().lte(90).gte(-90),
      z.number().lte(180).gte(-180),
    ]),
    provider: z.string(),
  })
  .partial({
    location: true,
    height: true,
  });
export type DeviceAttributeType = z.infer<typeof DeviceAttributeSchema>;

export const DeviceTypeAttributeSchema = AttributeSchema.pick({
  description: true,
  data_type: true,
  id: true,
  name: true,
  is_required: true,
});
export type DeviceTypeAttributeType = z.infer<typeof DeviceTypeAttributeSchema>;

export const DeviceTypeSchema = z.object({
  name: z.string(),
  id: z.string(),
  attributes: z.array(DeviceTypeAttributeSchema),
});
export type DeviceTypeType = z.infer<typeof DeviceTypeSchema>;

export const DeviceStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  ARCHIVED: "archived",
} as const;
export type DeviceStatusType = (typeof DeviceStatus)[keyof typeof DeviceStatus];

export const DeviceSchema = z
  .object({
    name: z.string(),
    status: z.enum([
      DeviceStatus.ACTIVE,
      DeviceStatus.INACTIVE,
      DeviceStatus.ARCHIVED,
    ]),
    type_id: z.string(),
    facility_id: IdentifierSchema,
    attributes: DeviceAttributeSchema,
    custom_attributes: CustomAttributeRecordSchema,
    facility: z.lazy(() =>
      FacilitySchema.pick({
        id: true,
        name: true,
      })
    ),
    measurement_types: z.array(z.string()),
    type: DeviceTypeSchema.pick({
      name: true,
      id: true,
    }),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema)
  .describe("Schema for Device asset type");
export const DeviceSchemaEnumKeys = DeviceSchema.keyof().Enum;
export type DeviceType = z.infer<typeof DeviceSchema>;

export const DeviceCreateOneRequestSchema = createCreateOneRequestSchema(
  DeviceSchema.pick({
    name: true,
    facility_id: true,
    type_id: true,
    status: true,
    measurement_types: true,
    attributes: true,
    custom_attributes: true,
  })
);
export type DeviceCreateOneRequestType = z.infer<
  typeof DeviceCreateOneRequestSchema
>;

export const DeviceGetListRequestSchema = createGetListRequestSchema(
  z
    .object({
      "facility.custom_attributes": z.record(z.unknown()),
      "facility.id": z.string(),
      "facility.name": z.string(),
      "facility.status": z.string(),
      attributes: z.record(z.unknown()),
      custom_attributes: z.record(z.unknown()),
      id: z.string().uuid().or(z.array(z.string().uuid())),
      measurement_types: z.string(),
      name: z.string(),
      status: z.string(),
      type_id: z.string(),
      "event.id": z.string().uuid(),
    })
    .partial(),
  z.object({
    period: DateTimeValueSchemas.dateYearMonth,
  })
);
export type DeviceGetListRequestType = z.infer<
  typeof DeviceGetListRequestSchema
>;
